import { Procedure, Process, UserOnboarding, UserOnboardingSteps } from "@shared/models";
import { call, put, takeLatest } from "redux-saga/effects";
import { PaginatedResponse, ActionWithPayload, Filter, BaseSuccessResponse, WithCallback } from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";
import {
  clearSemanticProcedures,
  createProcedure,
  deleteProcedure,
  getProcedure,
  getProcedures,
  getProcesses,
  getProcessesProcedures,
  getSemanticProcedures,
  getUserOnboardingSteps,
  updateProcedure,
  updateUserOnboardingStatus,
} from "@containers/Procedure/store/actions";
import {
  CreateProcedureDto,
  CreateProcedureSuccess,
  DeleteProcedureSuccess,
  SemanticSearchFilter,
  SemanticSearchResponse,
  UpdateProcedureDto,
} from "@containers/Procedure/interfaces";
import { ProcessFilter } from "@shared/interfaces/ProcessFilter.interface";

import api from "../api";

export function* getProceduresSaga({ payload }: ActionWithPayload<Filter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Procedure> = yield call(api.getProcedures, payload);
    yield put(stopLoading());
    yield put(
      getProcedures.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getProcedures.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getProcedureSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: Procedure = yield call(api.getProcedure, payload);
    yield put(stopLoading());
    yield put(getProcedure.success(response));
  } catch (error) {
    yield put(getProcedure.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getProcessesSaga() {
  try {
    yield put(startLoading());
    const response: Process[] = yield call(api.getProcesses);
    yield put(stopLoading());
    yield put(getProcesses.success(response));
  } catch (error) {
    yield put(getProcesses.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getProcessesProceduresSaga({ payload }: ActionWithPayload<ProcessFilter>) {
  try {
    yield put(startLoading());
    const response: Process[] = yield call(api.getProcessesProcedures, payload);
    yield put(stopLoading());
    yield put(getProcessesProcedures.success(response));
  } catch (error) {
    yield put(getProcessesProcedures.failure(error as Error));
    yield put(stopLoading());
  }
}

// Also registered (below) as a takeLatest target for clearSemanticProcedures, purely so that
// toggling semantic mode off cancels any still-in-flight search — otherwise a slow response could
// land after the user cleared/left semantic mode and silently repopulate stale results. This
// worker no-ops on that action; takeLatest's own cancellation of the previous task is the actual
// fix, this guard just stops the no-op invocation from trying to read a request payload that isn't there.
export function* getSemanticProceduresSaga(
  action: ActionWithPayload<SemanticSearchFilter> | ReturnType<typeof clearSemanticProcedures>,
) {
  if (action.type === clearSemanticProcedures().type) return;
  const { payload } = action as ActionWithPayload<SemanticSearchFilter>;

  try {
    yield put(startLoading());
    const response: SemanticSearchResponse = yield call(api.getSemanticProcedures, payload);
    yield put(stopLoading());
    yield put(getSemanticProcedures.success(response));
  } catch (error) {
    yield put(getSemanticProcedures.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* createProcedureSaga({ payload }: ActionWithPayload<CreateProcedureDto>) {
  try {
    yield put(startLoading());
    const response: CreateProcedureSuccess = yield call(api.createProcedure, payload);
    yield put(stopLoading());
    yield put(createProcedure.success(response));
  } catch (error) {
    yield put(createProcedure.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* updateProcedureSaga({ payload }: ActionWithPayload<UpdateProcedureDto>) {
  try {
    yield put(startLoading());
    const response: CreateProcedureSuccess = yield call(api.updateProcedure, payload);
    yield put(stopLoading());
    yield put(updateProcedure.success(response));
  } catch (error) {
    yield put(updateProcedure.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* deleteProcedureSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: DeleteProcedureSuccess = yield call(api.deleteProcedure, payload);
    yield put(stopLoading());
    yield put(deleteProcedure.success(response));
  } catch (error) {
    yield put(deleteProcedure.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getUserOnboardingStepsSaga() {
  try {
    yield put(startLoading());
    const response: UserOnboardingSteps[] = yield call(api.getUserOnboardingSteps);
    yield put(stopLoading());
    yield put(getUserOnboardingSteps.success(response));
  } catch (error) {
    yield put(getUserOnboardingSteps.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* updateUserOnboardingStatusSaga({ payload }: ActionWithPayload<WithCallback<Partial<UserOnboarding>>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: BaseSuccessResponse = yield call(api.updateUserOnboardingStatus, rest);
    yield put(stopLoading());
    yield put(updateUserOnboardingStatus.success(response));
    cb?.();
  } catch (error) {
    yield put(updateUserOnboardingStatus.failure(error as Error));
    yield put(stopLoading());
  }
}

function* procedureSagas() {
  yield takeLatest(getProcedures.request, getProceduresSaga);
  yield takeLatest(getProcedure.request, getProcedureSaga);
  yield takeLatest(getProcesses.request, getProcessesSaga);
  yield takeLatest(getProcessesProcedures.request, getProcessesProceduresSaga);
  yield takeLatest([getSemanticProcedures.request, clearSemanticProcedures], getSemanticProceduresSaga);
  yield takeLatest(createProcedure.request, createProcedureSaga);
  yield takeLatest(updateProcedure.request, updateProcedureSaga);
  yield takeLatest(deleteProcedure.request, deleteProcedureSaga);
  yield takeLatest(getUserOnboardingSteps.request, getUserOnboardingStepsSaga);
  yield takeLatest(updateUserOnboardingStatus.request, updateUserOnboardingStatusSaga);
}

export default procedureSagas;
